<script setup lang="ts">
import { computed } from "vue";
import { UiIcon, UiText } from "@shopexperts/shared";
import type { QuoteMessageCardTimelineStep } from "./QuoteMessageCard.types";
import { quoteCopy } from "@/v2Projects/utils/quoteCopy";

const props = defineProps<{
    steps: QuoteMessageCardTimelineStep[];
}>();

type StepKind = "complete" | "current" | "upcoming";

type RenderedStep = QuoteMessageCardTimelineStep & {
    kind: StepKind;
    isLast: boolean;
};

const renderedSteps = computed<RenderedStep[]>(() => {
    const firstIncompleteIndex = props.steps.findIndex((step) => !step.complete);
    return props.steps.map((step, index, arr) => ({
        ...step,
        isLast: index === arr.length - 1,
        kind: step.complete
            ? "complete"
            : index === firstIncompleteIndex
                ? "current"
                : "upcoming",
    }));
});

// "current" lines transition from dark to gray to communicate progress toward the next milestone.
const HORIZONTAL_CURRENT_GRADIENT =
    "bg-[linear-gradient(to_right,var(--color-se-dark)_50%,var(--color-se-gray-300)_50%)]";
const VERTICAL_CURRENT_GRADIENT =
    "bg-[linear-gradient(to_bottom,var(--color-se-dark)_50%,var(--color-se-gray-300)_50%)]";

function lineClass(kind: StepKind, axis: "horizontal" | "vertical"): string {
    if (kind === "complete") return "bg-se-green";
    if (kind === "upcoming") return "bg-se-gray-300";
    return axis === "horizontal" ? HORIZONTAL_CURRENT_GRADIENT : VERTICAL_CURRENT_GRADIENT;
}
</script>

<template>
    <section class="flex flex-col gap-5 max-sm:gap-3">
        <UiText as="h3" variant="p" class="font-semibold text-se-dark">
            {{ quoteCopy.proposalDetails.timelineTitle }}
        </UiText>

        <ol class="grid grid-cols-1 gap-y-4 sm:grid-cols-4 sm:gap-y-0 max-sm:gap-y-0">
            <li
                v-for="step in renderedSteps"
                :key="step.label"
                class="relative flex gap-3 sm:flex-col sm:items-center sm:gap-0 sm:text-center
                    max-sm:items-center max-sm:gap-3.5 max-sm:py-2.5 max-sm:min-h-13"
            >
                <span
                    v-if="!step.isLast"
                    aria-hidden="true"
                    class="absolute left-3 top-1/2 h-full w-px sm:hidden"
                    :class="lineClass(step.kind, 'vertical')"
                />

                <span
                    v-if="!step.isLast"
                    aria-hidden="true"
                    class="absolute left-1/2 top-4 hidden h-0.5 w-full -translate-y-1/2 sm:block"
                    :class="lineClass(step.kind, 'horizontal')"
                />

                <div
                    class="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-4 ring-white
                        max-sm:h-6 max-sm:w-6 max-sm:ring-0"
                    :class="
                        step.kind === 'complete'
                            ? 'bg-se-green text-white'
                            : step.kind === 'current'
                                ? 'bg-se-dark text-white'
                                : 'bg-se-gray-300'
                    "
                >
                    <UiIcon v-if="step.kind === 'complete'" name="check" class="h-4 w-4 max-sm:h-3.5 max-sm:w-3.5" />
                    <span
                        v-else-if="step.kind === 'current'"
                        class="h-2.5 w-2.5 rounded-full bg-white max-sm:h-2 max-sm:w-2"
                    />
                </div>

                <div class="sm:mt-3 flex flex-col max-sm:flex-1 max-sm:min-w-0">
                    <UiText as="p" variant="p-small" class="font-semibold text-se-dark sm:inline max-sm:hidden">
                        {{ step.label }}
                    </UiText>
                    <UiText as="p" variant="p-small" class="mt-0.5 text-se-gray-500 sm:inline max-sm:hidden">
                        {{ step.value }}
                    </UiText>

                    <UiText as="p" variant="p" class="font-semibold text-se-dark hidden max-sm:block">
                        {{ step.label }}
                    </UiText>
                    <UiText as="p" variant="h6" class="text-se-gray-500 hidden max-sm:block">
                        {{ step.value }}
                    </UiText>
                </div>
            </li>
        </ol>
    </section>
</template>
